import React from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Button, IconButton, InputAdornment, Typography } from '@mui/material';

import { PasswordFieldChecker } from '~/components/common';
import { isInvalidPassword } from '~/components/common/PasswordFieldChecker/helpers';
import { TextField } from '~/components/common/form';
import { useLanguage } from '~/hooks/language';

interface Props {
  inviteeName?: string;
  password: string;
  isSubmitDisabled: boolean;
  onContinue: () => void;
}

export const CreatePassword: React.FC<Props> = props => {
  const [passwordFieldType, setPasswordFieldType] = React.useState<'password' | 'text'>('password');
  const [confirmPasswordFieldType, setConfirmPasswordFieldType] = React.useState<'password' | 'text'>('password');
  const { onContinue, password, isSubmitDisabled, inviteeName = '' } = props;
  const { translate } = useLanguage('ua');

  return (
    <Box>
      {/* Fields */}
      <Typography variant="h2" fontWeight={400} sx={{ mb: '10px' }}>
        {translate('invite.createPassword.title')}
      </Typography>

      <Typography
        variant="h5"
        fontWeight={400}
        // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
        dangerouslySetInnerHTML={{
          __html: translate('invite.createPassword.subtitle', {
            name: `<span>${inviteeName}</span>`,
          }),
        }}
        sx={{
          '& span': {
            fontWeight: 600,
            color: 'text.primary',
          },
        }}
      />

      <TextField
        name="password"
        label={translate('invite.createPassword.password.placeholder')}
        autoComplete="current-password"
        type={passwordFieldType}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={() => {
                setPasswordFieldType(passwordFieldType === 'password' ? 'text' : 'password');
              }}
            >
              {passwordFieldType === 'password' ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        }
      />

      <TextField
        name="confirmPassword"
        label={translate('invite.createPassword.confirmPassword.placeholder')}
        autoComplete="current-confirm-password"
        type={confirmPasswordFieldType}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={() => {
                setConfirmPasswordFieldType(confirmPasswordFieldType === 'password' ? 'text' : 'password');
              }}
            >
              {confirmPasswordFieldType === 'password' ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        }
      />

      {/* Checker */}
      <Box sx={{ mt: '30px', mb: '30px' }}>
        <PasswordFieldChecker value={password} />
      </Box>

      {/* Submit */}
      <Button
        type="button"
        variant="contained"
        fullWidth
        disabled={isSubmitDisabled || isInvalidPassword(password)}
        onClick={onContinue}
      >
        {translate('invite.createPassword.submit')}
      </Button>
    </Box>
  );
};
